/**
 * Tema styled-components dell'applicazione.
 *
 * I valori sono riferimenti alle variabili CSS definite in
 * `styles/design-system.css`, che resta l'unica fonte dei token:
 * modificare un colore lì aggiorna sia i CSS sia gli styled-components.
 *
 * Uso nei componenti:
 *   const Box = styled.div`
 *     color: ${({ theme }) => theme.colors.textPrimary};
 *   `;
 */

export const theme = {
  colors: {
    // Primario (verde oliva brand)
    primary50: 'var(--primary-50)',
    primary100: 'var(--primary-100)',
    primary200: 'var(--primary-200)',
    primary300: 'var(--primary-300)',
    primary400: 'var(--primary-400)',
    primary500: 'var(--primary-500)',
    primary600: 'var(--primary-600)',
    primary700: 'var(--primary-700)',
    primary800: 'var(--primary-800)',
    primary900: 'var(--primary-900)',

    // Accento terracotta
    accent100: 'var(--accent-100)',
    accent300: 'var(--accent-300)',
    accent500: 'var(--accent-500)',
    accent600: 'var(--accent-600)',
    accent700: 'var(--accent-700)',

    // Brand di supporto
    brandCream: 'var(--brand-cream)',
    brandBrown: 'var(--brand-brown)',
    brandCharcoal: 'var(--brand-charcoal)',

    // Neutri
    gray50: 'var(--gray-50)',
    gray100: 'var(--gray-100)',
    gray200: 'var(--gray-200)',
    gray300: 'var(--gray-300)',
    gray400: 'var(--gray-400)',
    gray500: 'var(--gray-500)',
    gray600: 'var(--gray-600)',
    gray700: 'var(--gray-700)',
    gray800: 'var(--gray-800)',
    gray900: 'var(--gray-900)',

    // Sistema
    success: 'var(--success)',
    warning: 'var(--warning)',
    error: 'var(--error)',
    info: 'var(--info)',
    successSoft: 'var(--success-soft)',
    warningSoft: 'var(--warning-soft)',
    errorSoft: 'var(--error-soft)',
    infoSoft: 'var(--info-soft)',

    // Testo e sfondi
    textPrimary: 'var(--text-primary)',
    textSecondary: 'var(--text-secondary)',
    textMuted: 'var(--text-muted)',
    bgPrimary: 'var(--bg-primary)',
    bgSecondary: 'var(--bg-secondary)',
    cardBg: 'var(--card-bg)',
    borderColor: 'var(--border-color)',
  },

  font: {
    family: 'var(--font-family)',
    mono: 'var(--font-family-mono)',
    sizeXs: 'var(--font-size-xs)',
    sizeSm: 'var(--font-size-sm)',
    sizeBase: 'var(--font-size-base)',
    sizeLg: 'var(--font-size-lg)',
    sizeXl: 'var(--font-size-xl)',
    size2xl: 'var(--font-size-2xl)',
    size3xl: 'var(--font-size-3xl)',
    weightNormal: 'var(--font-weight-normal)',
    weightMedium: 'var(--font-weight-medium)',
    weightSemibold: 'var(--font-weight-semibold)',
    weightBold: 'var(--font-weight-bold)',
  },

  space: {
    xs: 'var(--space-xs)',
    sm: 'var(--space-sm)',
    md: 'var(--space-md)',
    lg: 'var(--space-lg)',
    xl: 'var(--space-xl)',
    xxl: 'var(--space-2xl)',
  },

  radius: {
    sm: 'var(--border-radius-sm)',
    md: 'var(--border-radius-md)',
    lg: 'var(--border-radius-lg)',
    xl: 'var(--border-radius-xl)',
  },

  shadow: {
    sm: 'var(--shadow-sm)',
    md: 'var(--shadow-md)',
    lg: 'var(--shadow-lg)',
  },

  transition: {
    fast: 'var(--transition-fast)',
    normal: 'var(--transition-normal)',
    slow: 'var(--transition-slow)',
  },
} as const;

export type AppTheme = typeof theme;
